import React from 'react';
import Grid from '@mui/material/Grid';
import { useFieldArray } from 'react-hook-form';
import PaymentMeansSelect from './PaymentMeansSelect';
import CustomTextInput from '../CustomTextInput';
import { cardPaymentCodes, paymentFinancialAccountCodes, paymentMandateCodes } from '../../constants/appConstants';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const PaymentMeansFields = ({ control, errors, readOnly = false, disabled = false }) => {
  const { fields: paymentMeansFields, replace } = useFieldArray({
    control,
    name: 'payment_means',
  });

  const { eInvoiceForm } = useTranslatedMessages();
  const { payment_means_fields: messages } = eInvoiceForm ?? {};

  const selectedCodes = paymentMeansFields.map((payment) => payment.payment_means_code);

  const handlePaymentMeansChange = (selectedCodes) => {
    const updatedPaymentMeans = selectedCodes.map((code) => {
      const existing = paymentMeansFields.find((payment) => payment.payment_means_code === code);

      return (
        existing || {
          payment_means_code: code,
          payment_id: [],
          card_account: null,
          payee_financial_account: null,
          payment_mandate: null,
        }
      );
    });

    replace(updatedPaymentMeans);
  };

  const cardOwnerIndex = paymentMeansFields.findIndex((payment) =>
    cardPaymentCodes.includes(payment.payment_means_code),
  );

  return (
    <>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <PaymentMeansSelect
          data-testid="payment_means_code-select"
          id="payment_means_code"
          name="payment_means_code"
          value={selectedCodes}
          errors={errors}
          readOnly={readOnly}
          disabled={disabled}
          onChange={handlePaymentMeansChange}
          helperText=""
        />
      </Grid>

      {cardOwnerIndex !== -1 && (
        <>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextInput
              data-testid="primary_account_number_id-input"
              name={`payment_means.${cardOwnerIndex}.card_account.primary_account_number_id`}
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={disabled}
              label={messages?.pan_label}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextInput
              data-testid="network_id-input"
              name={`payment_means.${cardOwnerIndex}.card_account.network_id`}
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={disabled}
              label={messages?.network_id_label}
            />
          </Grid>
        </>
      )}

      {paymentMeansFields.map((payment, index) => {
        const code = payment.payment_means_code;

        return (
          <React.Fragment key={payment.id}>
            {paymentFinancialAccountCodes.includes(code) && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomTextInput
                  data-testid="payeeFinancialAccount-input"
                  name={`payment_means.${index}.payee_financial_account.id`}
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  disabled={disabled}
                  label={messages?.payment_account_identifier_label}
                />
              </Grid>
            )}

            {paymentMandateCodes.includes(code) && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <CustomTextInput
                    data-testid="payment_mandate-input"
                    name={`payment_means.${index}.payment_mandate.id`}
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    disabled={disabled}
                    label={messages?.mandate_reference_identifier_label}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <CustomTextInput
                    data-testid="payer_financial_account_id"
                    name={`payment_means.${index}.payment_mandate.payer_financial_account_id`}
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    disabled={disabled}
                    label={messages?.payer_financial_account_identifier_label}
                  />
                </Grid>
              </>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default PaymentMeansFields;
